import { FC, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import cn from 'bem-cn';

import { RoutePaths } from '../../constants';
import { isLightThemeRoute } from '../../utils/theme';
import { Stack } from '../../components/UI';
import { Header, HeaderElements } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { Navigation, NavigationElement, NavigationElements } from '../../components/Navigation';

import { WebAppLayoutProps } from './WebAppLayout.types';
import './WebAppLayout.scss';

const b = cn('web-app-layout');

const WebAppLayoutContent: FC<WebAppLayoutProps> = ({
    children,
    headerElements,
    navigationElements,
    hideNavigation,
}) => {
    const location = useLocation();
    const footerRef = useRef<HTMLDivElement>(null);

    const isMainPage = location.pathname === RoutePaths.Main || location.pathname.startsWith(`${RoutePaths.Category}/`);
    const hideMobileSearch = isMainPage || [RoutePaths.Study, RoutePaths.Compete].includes(location.pathname);
    const hideFooter = isMainPage || location.pathname === RoutePaths.Search;
    const isLightColors = isLightThemeRoute(location.pathname);

    // Дефолтные элементы шапки
    const defaultHeaderElements: HeaderElements = {
        desktop: {
            left: { type: 'logo' },
            center: { type: 'search' },
            right: { type: 'profile' },
        },
        mobile: {
            left: { type: hideMobileSearch ? 'logo' : 'search' },
            center: hideMobileSearch ? undefined : { type: 'logo' },
            right: { type: 'profile' },
        },
    };

    const getNavigationElementsByPosition = (): NavigationElement[] => {
        return [
            {
                text: 'Вдохновиться',
                icon: 'inspirationAnimated',
                active: isMainPage,
                to: RoutePaths.Main,
            },
            {
                text: 'Учиться',
                icon: 'learnAnimated',
                active: location.pathname === RoutePaths.Study,
                to: RoutePaths.Study,
            },
            {
                text: 'Соревноваться',
                icon: 'competeAnimated',
                active: location.pathname === RoutePaths.Compete,
                to: RoutePaths.Compete,
            },
            {
                text: 'Предложить промпт',
                icon: 'plus',
                accent: true,
                to: RoutePaths.NewPrompt,
            },
        ];
    };

    // Дефолтные навигационные элементы
    const defaultNavigationElements: NavigationElements = {
        desktop: getNavigationElementsByPosition(),
        mobile: getNavigationElementsByPosition(),
    };

    return (
        <Stack className={b()}>
            <div className={b('container')}>
                <Header elements={headerElements || defaultHeaderElements} isLightColors={isLightColors} />
                <div className={b('content-container', { 'navigation-hidden': hideNavigation })}>{children}</div>
                {!hideNavigation && (
                    <Navigation
                        elements={navigationElements || defaultNavigationElements}
                        footerRef={!hideFooter ? footerRef : undefined}
                    />
                )}
            </div>
            {!hideFooter && <Footer ref={footerRef} isLightColors={isLightColors} />}
        </Stack>
    );
};

export const WebAppLayout: FC<WebAppLayoutProps> = props => {
    return <WebAppLayoutContent {...props} />;
};
